import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { SelectOptionLoaderI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers/index';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { AddedValueFieldConfig } from '@ordersnproducts/modules/order-builder/modules/rms-specification/models/form-added-value-field-config';
import { getRmsRehydrate } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/index';
import { getAddedValuesOpenPlatformState } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import { AddedValueTuple } from '@ordersnproducts/modules/order-builder/modules/rms-specification/reducers/open-platform/added-values.reducer';

const addedValueAndRehydrate = createSelector(getAddedValuesOpenPlatformState, getRmsRehydrate, (state, rehydate) => {
  return {
    state,
    rehydate,
  };
});

@Injectable()
export class AddedValuesLoaderOpenPlatformService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of paymentTerms
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup, config: AddedValueFieldConfig): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(addedValueAndRehydrate),
      filter(({ state }) => {
        const selectedContract =
          formGroup.get(SpecFieldNames.CONTRACT_SELECT) && formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
        const formId = formGroup.get(SpecFieldNames.ID).value;
        const keyName = `${selectedContract}_${formId}`;
        return !!state.availableAddedValues[keyName];
      }),
      map(({ state }) => {
        const selectedContract =
          formGroup.get(SpecFieldNames.CONTRACT_SELECT) && formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
        const formId = formGroup.get(SpecFieldNames.ID).value;
        const keyName = `${selectedContract}_${formId}`;
        const availableAddedValues = state.availableAddedValues[keyName];
        return this.getAddedValuesToShow(availableAddedValues, config.index);
      }),
    );
  }

  /**
   * Returns them as combobox options
   * @param tuples
   * @param index
   */
  private getAddedValuesToShow(tuples: AddedValueTuple[], index: number): SelectOptionI[] {
    if (tuples[index] && tuples[index].addedValues) {
      // options is all vaps available
      return tuples[index].addedValues.map((addedValue) => {
        return {
          id: addedValue.valueAddedCode,
          name: `${addedValue.valueAddedCode.replace(/^0+/, '')} / ${addedValue.valueAddedDesc}`,
        };
      });
    } else {
      return [];
    }
  }
}

@Injectable()
export class AddedValuesLoaderProductOpenPlatformService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of paymentTerms
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup, config: AddedValueFieldConfig): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(addedValueAndRehydrate),
      filter(({ state }) => {
        const formId = formGroup.get(SpecFieldNames.ID).value;
        const keyName = `${'undefined'}_${formId}`;
        return !!state.availableAddedValues[keyName];
      }),
      map(({ state }) => {
        const formId = formGroup.get(SpecFieldNames.ID).value;
        const keyName = `${'undefined'}_${formId}`;
        const availableAddedValues = state.availableAddedValues[keyName];
        return this.getAddedValuesToShow(availableAddedValues, config.index);
      }),
    );
  }

  /**
   * Returns them as combobox options
   * @param tuples
   * @param index
   */
  private getAddedValuesToShow(tuples: AddedValueTuple[], index: number): SelectOptionI[] {
    if (tuples[index] && tuples[index].addedValues) {
      // options is all vaps available
      return tuples[index].addedValues.map((addedValue) => {
        return {
          id: addedValue.valueAddedCode,
          name: `${addedValue.valueAddedCode.replace(/^0+/, '')} / ${addedValue.valueAddedDesc}`,
        };
      });
    } else {
      return [];
    }
  }
}
